import { IntuitiveToothForTreatment } from '@modules/rx-intuitive-teeth-orchestrator/models';
import { isImplantIndication } from "@shared/utils/procedure-type-util";

const SEP = ' · ';

export const TREATMENT_PREVIEW_LINE_EMPTY = '\u2014';

const segment = (value: unknown): string | null => {
	if (value === null || value === undefined || value === -1) {
		return null;
	}

	if (typeof value === 'string') {
		const t = value.trim();

		return t.length > 0 ? t : null;
	}

	return String(value);
};

const collectTreatmentPreviewParts = (tooth: IntuitiveToothForTreatment): string[] =>
	[segment(tooth.specification?.Name), segment(tooth.material?.Name), segment(tooth.shadeSystem?.Name), segment(tooth.shadeBody)].filter(
		(p): p is string => p != null && p.length > 0
	);

/**
 * Single-line summary for compact prep cards (uses resolved IdName labels from the diagram VM).
 */
export const buildTreatmentPreviewLine = (tooth: IntuitiveToothForTreatment): string => {
	const parts = collectTreatmentPreviewParts(tooth);

	return parts.length > 0 ? parts.join(SEP) : TREATMENT_PREVIEW_LINE_EMPTY;
};

export const buildCopyFromSourcePreviewLine = (tooth: IntuitiveToothForTreatment): string => {
	const crownParts = collectTreatmentPreviewParts(tooth);
	const crownLine = crownParts.length > 0 ? crownParts.join(SEP) : '';
	const implantPart = segment(tooth.implantsTreatmentSummary);

	if (isImplantIndication(tooth.indicationId) && implantPart != null) {
		return crownLine.length > 0 ? `${implantPart}${SEP}${crownLine}` : implantPart;
	}

	return crownLine.length > 0 ? crownLine : TREATMENT_PREVIEW_LINE_EMPTY;
};

/**
 * Builds a treatment preview line for a saved bridge.
 *
 * Rules:
 * - Specification, Material, Shade system: shown only if ALL teeth in bridge have the same value
 * - Body (shadeBody): shown for ALL unique values across the bridge (comma-separated if multiple)
 * - If a property differs across teeth, it's hidden from the summary
 *
 * @param bridgeTeeth All teeth that are part of the bridge
 * @returns Treatment preview line for the bridge summary
 */
export const buildBridgePreviewLine = (bridgeTeeth: IntuitiveToothForTreatment[]): string => {
	if (bridgeTeeth.length === 0) {
		return TREATMENT_PREVIEW_LINE_EMPTY;
	}

	const uniqueSpecifications = new Set<string>();
	const uniqueMaterials = new Set<string>();
	const uniqueShadeSystems = new Set<string>();
	const uniqueShadeBodies = new Set<string>();

	// Collect all values in a single pass
	for (const tooth of bridgeTeeth) {
		uniqueSpecifications.add(tooth.specification?.Name ?? '');
		uniqueMaterials.add(tooth.material?.Name ?? '');
		uniqueShadeSystems.add(tooth.shadeSystem?.Name ?? '');
		uniqueShadeBodies.add(tooth.shadeBody?.trim() ?? '');
	}

	const parts: string[] = [];

	// Show specification only if all teeth have the same value
	if (uniqueSpecifications.size === 1 && !uniqueSpecifications.has('')) {
		parts.push(...uniqueSpecifications);
	}

	// Show material only if all teeth have the same value
	if (uniqueMaterials.size === 1 && !uniqueMaterials.has('')) {
		parts.push(...uniqueMaterials);
	}

	// Show shade system only if all teeth have the same value
	if (uniqueShadeSystems.size === 1 && !uniqueShadeSystems.has('')) {
		parts.push(...uniqueShadeSystems);
	}

	const filteredBodies = [...uniqueShadeBodies].filter(b => !!b);

	// Show all unique shade bodies (exception - shown even if different)
	if (filteredBodies.length > 0) {
		parts.push(filteredBodies.join(', '));
	}

	return parts.length > 0 ? parts.join(SEP) : TREATMENT_PREVIEW_LINE_EMPTY;
};
